import { StandardEvents } from '@shopify/events';

(() => {
  if (window.n4MobileGallery) { window.n4MobileGallery.init(document); return; }
  const controllers = new Map();
  let dialog;
  let active;
  let returnFocus;
  const safeUrl = value => {
    try { const url = new URL(value, document.baseURI); return /^https?:$/.test(url.protocol) ? url.href : ''; }
    catch { return ''; }
  };
  function button(label, text, action) {
    const el = document.createElement('button');
    el.type = 'button'; el.className = 'n4-gallery__step'; el.setAttribute('aria-label', label);
    el.textContent = text; el.addEventListener('click', action); return el;
  }
  function ensureDialog(labels) {
    if (!('HTMLDialogElement' in window)) return null;
    if (!dialog) {
      dialog = document.createElement('dialog'); dialog.className = 'n4-image-dialog';
      const header = document.createElement('div'); header.className = 'n4-image-dialog__header';
      const title = document.createElement('p'); title.className = 'n4-image-dialog__title';
      header.append(title, button(labels.labelClose, '×', () => dialog.close()));
      const frame = document.createElement('div'); frame.className = 'n4-image-dialog__frame';
      const image = document.createElement('img'); image.alt = ''; frame.append(image);
      const footer = document.createElement('div'); footer.className = 'n4-image-dialog__footer';
      const counter = document.createElement('span'); counter.className = 'n4-image-dialog__counter'; counter.setAttribute('aria-live', 'polite');
      footer.append(button(labels.labelPrevious, '←', () => active?.stepImage(-1)), counter, button(labels.labelNext, '→', () => active?.stepImage(1)));
      dialog.append(header, frame, footer); document.body.append(dialog);
      dialog.addEventListener('close', () => { active = null; if (returnFocus?.isConnected) returnFocus.focus(); });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); active?.stepImage((event.key === 'ArrowRight' ? 1 : -1) * (active?.rtl ? -1 : 1));
        }
        if (event.key === 'Tab') {
          const items = [...dialog.querySelectorAll('button')];
          if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1).focus(); }
          else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus(); }
        }
      });
    }
    dialog.setAttribute('aria-label', labels.labelViewer);
    dialog.querySelector('.n4-image-dialog__title').textContent = labels.labelViewer;
    const buttons = dialog.querySelectorAll('button');
    [labels.labelClose, labels.labelPrevious, labels.labelNext].forEach((label, i) => buttons[i].setAttribute('aria-label', label));
    return dialog;
  }
  function updateDialog(controller) {
    if (!dialog?.open || active !== controller || !controller.image) return;
    const image = dialog.querySelector('img');
    image.onerror = () => { image.onerror = null; image.src = controller.image.currentSrc || controller.image.src; };
    image.src = controller.zoom.href; image.alt = controller.image.alt;
    const photos = controller.buttons.filter(el => el.dataset.n4Type === 'image');
    dialog.querySelector('.n4-image-dialog__counter').textContent = `${photos.indexOf(controller.buttons[controller.index]) + 1} / ${photos.length}`;
  }
  function mount(gallery) {
    if (controllers.has(gallery)) return;
    const image = gallery.querySelector('.n4-gallery__main-image');
    const zoom = gallery.querySelector('[data-n4-zoom]');
    const buttons = [...gallery.querySelectorAll('[data-n4-media]')];
    const stage = gallery.querySelector('.n4-gallery__stage');
    if (!buttons.length || !stage) return;
    const abort = new AbortController(); const { signal } = abort;
    const labels = gallery.dataset;
    const panels = [...gallery.querySelectorAll('[data-n4-video-panel]')];
    const controller = { gallery, image, zoom, buttons, abort, index: Math.max(0, buttons.findIndex(el => el.getAttribute('aria-pressed') === 'true')), sequence: 0, timer: null, pending: null, rtl: getComputedStyle(gallery).direction === 'rtl' };
    controllers.set(gallery, controller);
    if (zoom && 'HTMLDialogElement' in window) zoom.setAttribute('aria-haspopup', 'dialog');
    const status = document.createElement('p'); status.className = 'n4-gallery__status'; status.setAttribute('role', 'status'); gallery.append(status);
    const pause = () => panels.forEach(panel => panel.querySelector('video')?.pause());
    controller.pause = pause;
    const activate = (index, focus) => {
      const selected = buttons[index];
      const isVideo = selected.dataset.n4Type === 'video';
      pause();
      panels.forEach(panel => { panel.hidden = !isVideo || panel.dataset.n4VideoPanel !== selected.dataset.n4MediaId; });
      if (zoom) zoom.hidden = isVideo;
      const zoomLabel = gallery.querySelector('.n4-gallery__zoom-label');
      if (zoomLabel) zoomLabel.hidden = isVideo;
      stage.classList.toggle('n4-gallery__stage--video', isVideo);
      controller.index = index; controller.pending = null;
      buttons.forEach(el => el.setAttribute('aria-pressed', String(el === selected)));
      gallery.querySelector('[data-n4-count]').textContent = `${index + 1} / ${buttons.length}`;
      gallery.querySelector('[data-n4-caption]').textContent = selected.dataset.n4Alt;
      status.textContent = '';
      if (focus) selected.focus({ preventScroll: true });
      const rail = selected.parentElement;
      const box = selected.getBoundingClientRect(); const railBox = rail.getBoundingClientRect();
      if (box.left < railBox.left || box.right > railBox.right) rail.scrollBy({ left: box.left - railBox.left - (railBox.width - box.width) / 2, behavior: 'auto' });
      updateDialog(controller);
    };
    controller.select = (index, focus = false) => {
      index = (index + buttons.length) % buttons.length;
      const selected = buttons[index];
      clearTimeout(controller.timer);
      const ticket = ++controller.sequence;
      controller.pending = index;
      image?.removeAttribute('aria-busy');
      if (selected.dataset.n4Type === 'video') { activate(index, focus); return; }
      const url = safeUrl(selected.dataset.n4Full);
      if (!url || !image || !zoom) { controller.pending = null; return; }
      image.setAttribute('aria-busy', 'true'); status.textContent = labels.labelLoading;
      const preload = new Image();
      const fail = () => {
        if (ticket !== controller.sequence || signal.aborted) return;
        clearTimeout(controller.timer); ++controller.sequence; controller.pending = null;
        image.removeAttribute('aria-busy'); status.textContent = labels.labelError;
      };
      controller.timer = setTimeout(fail, 15000);
      preload.onload = () => {
        if (ticket !== controller.sequence || signal.aborted) return;
        clearTimeout(controller.timer);
        image.srcset = selected.dataset.n4Srcset || '';
        image.src = url; image.alt = selected.dataset.n4Alt; image.removeAttribute('aria-busy');
        zoom.href = safeUrl(selected.dataset.n4ZoomUrl) || url;
        activate(index, focus);
      };
      preload.onerror = fail;
      preload.sizes = image.sizes;
      preload.srcset = selected.dataset.n4Srcset || '';
      preload.src = url;
    };
    controller.step = direction => controller.select((controller.pending ?? controller.index) + direction);
    controller.stepImage = direction => {
      let index = controller.pending ?? controller.index;
      for (let i = 0; i < buttons.length; i++) {
        index = (index + direction + buttons.length) % buttons.length;
        if (buttons[index].dataset.n4Type === 'image') { controller.select(index); return; }
      }
    };
    buttons.forEach((el, index) => el.addEventListener('click', () => controller.select(index), { signal }));
    gallery.querySelector('.n4-gallery__thumbs')?.addEventListener('keydown', event => {
      const index = buttons.indexOf(event.target); if (index < 0) return;
      const forward = controller.rtl ? -1 : 1;
      const map = { ArrowRight: index + forward, ArrowLeft: index - forward, Home: 0, End: buttons.length - 1 };
      if (Object.hasOwn(map, event.key)) { event.preventDefault(); controller.select(map[event.key], true); }
    }, { signal });
    if (buttons.length > 1) {
      const controls = document.createElement('div'); controls.className = 'n4-gallery__controls';
      controls.append(button(labels.labelPrevious, controller.rtl ? '→' : '←', () => controller.step(-1)), button(labels.labelNext, controller.rtl ? '←' : '→', () => controller.step(1)));
      gallery.querySelector('.n4-gallery__hint').append(controls);
    }
    let pointer; let suppressClickUntil = 0;
    stage.addEventListener('pointerdown', event => {
      pointer = event.pointerType === 'touch' && event.isPrimary && !stage.classList.contains('n4-gallery__stage--video') ? { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() } : null;
    }, { signal, passive: true });
    stage.addEventListener('pointercancel', () => { pointer = null; }, { signal, passive: true });
    stage.addEventListener('pointerup', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x; const dy = event.clientY - pointer.y;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2 && performance.now() - pointer.time < 1200) {
        suppressClickUntil = performance.now() + 400; controller.step((dx < 0 ? 1 : -1) * (controller.rtl ? -1 : 1));
      }
      pointer = null;
    }, { signal, passive: true });
    zoom?.addEventListener('click', event => {
      if (performance.now() < suppressClickUntil) { event.preventDefault(); return; }
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      const viewer = ensureDialog(labels); if (!viewer) return;
      event.preventDefault(); active = controller; returnFocus = zoom;
      viewer.showModal(); viewer.querySelector('button').focus({ preventScroll: true }); updateDialog(controller);
    }, { signal });
    let variantRequest = 0;
    gallery.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, event => {
      if (event.target.closest?.('product-card') || !event.promise) return;
      const ticket = ++variantRequest;
      event.promise.then(({ detail }) => {
        if (signal.aborted || ticket !== variantRequest || String(detail?.productId) !== gallery.dataset.productId) return;
        const index = buttons.findIndex(el => el.dataset.n4MediaId === String(detail?.resource?.featured_media?.id));
        if (index >= 0) controller.select(index);
      }).catch(() => {});
    }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); }, { signal });
    panels.forEach(panel => panel.querySelector('video')?.addEventListener('error', () => { status.textContent = labels.labelError; }, { signal }));
    if (!image) activate(0, false);
  }
  function init(root) {
    cleanup();
    if (root.matches?.('[data-n4-gallery]')) mount(root);
    root.querySelectorAll?.('[data-n4-gallery]').forEach(mount);
  }
  function cleanup(root) {
    for (const [gallery, controller] of controllers) {
      if (!gallery.isConnected || root === gallery || root?.contains?.(gallery)) {
        if (active === controller && dialog?.open) dialog.close();
        controller.pause(); clearTimeout(controller.timer); ++controller.sequence;
        controller.abort.abort(); controllers.delete(gallery);
      }
    }
  }
  window.n4MobileGallery = { init };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
})();
